// Copyright (C) Siemens AG 2026. All rights reserved. Confidential
// Guard for /userSettings and /myOrders:
// If the user has no portal account, check orders:
//   - has orders  → allow through to /myOrders (redirect there from /userSettings)
//   - no orders   → redirect to /getstarted
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { authPortalUserState } from '../app-states/app.state';
import { CreditService } from '../services/credit/credit.service';

@Injectable({
  providedIn: 'root'
})
export class OrdersOrAccountGuard {

  constructor(private store: Store, private router: Router, private creditService: CreditService) {}

  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.store.pipe(
      select(authPortalUserState),
      filter(s => s.status === 'loaded'),
      take(1),
      switchMap(authState => {
        if (authState.authUser) {
          // Has portal account — allow through
          return of(true);
        }
        // No portal account — check if orders exist
        return this.creditService.getMyOrders().pipe(
          take(1),
          map(response => {
            const hasOrders = (response.tenantOrders?.length > 0) || (response.creditKeyOrders?.length > 0);
            if (hasOrders) {
              // If already navigating to myOrders, allow through; otherwise redirect there
              if (state.url.startsWith('/myOrders') || state.url === '/myOrders') {
                return true;
              }
              this.router.navigate(['myOrders']);
              return false;
            } else {
              this.router.navigate(['getstarted']);
              return false;
            }
          }),
          catchError(() => {
            this.router.navigate(['getstarted']);
            return of(false);
          })
        );
      })
    );
  }
}
